import { AbstractControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { SelectOption } from '@carm/shared/components/form-elements/data/select-options';
import * as dayjs from 'dayjs';
import { setDateWithOffset } from '@carm/shared/functions/common-functions';
import { IErrorCode } from '@carm/_utils/models';
import { dateFormat } from '@carm/shared/constants/const-carm';
import * as lpn from 'google-libphonenumber';

// eslint-disable-next-line max-len
export const EMAIL_VALIDATION_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
export const EMAIL_VALIDATOR = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,63}$/
export const B9_VALIDATION_REGEXP = /^[0-9]{9}$/;
export const B3_VALIDATION_REGEXP = /^[0-9]{14}$/;
export const BN15_VALIDATION_REGEXP = /^[0-9]{9}[Rr][Mm]\d{4}$/;
export const BUSINESS_PROGRAM_NO_REGEXP = /^[Rr][Mm]\d{4}$/;
export const REGISTRATION_CARRIER_PROGRAMID_REGEXP = /^[a-zA-Z0-9-]{4}$/;
export const REGISTRATION_WAREHOUSE_PROGRAMID_REGEXP = /^[a-zA-Z0-9]{4}$/;
export const REGISTRATION_DUTYFREESHOP_PROGRAMID_REGEXP =
  /^[0-9]{3}\s[A-Z]{3}\s[0-9]{3}$/;
export const BUSINESS_PROGRAM_NO_ONLY_REGEXP = /^[0-9]{4}$/;
export const ALPHANUMERIC_REGEX = /^([a-zA-Z0-9]+)$/;
export const NUMERIC_REGEX = /^-?\d+\.?\d*$/;
export const FAX_REGEX = /^[0-9.-]*$/;
export const THIRTEENDIGITS_REGEX = /^\d{1,13}(\.\d\d?)?$/;
export const PERCENTAGE_REGEX = /^\d{1,2}(\.\d\d?)?%?$/;
export const CLASSIFICATION_REGEX = /\d{4}\.\d{2}\.\d{2}\.\d{2}$/;
export const DATE_REGEX = /^\d{4}\-\d{2}\-\d{2}$/;
export const DATE_REGEX_DIGITS = /^\d{8}$/;
export const HAS_AT_LEAST_1_ALPHA_CHARACTER_REGEX = /^.*[a-zA-Z]+.*$/;
export const HAS_AT_LEAST_1_ALPHANUM_CHARACTER_REGEX = /^.*\w+.*$/;
export const ZIP_CODE_REGEX = /^(\d{5}|\d{8})?$/;
export const ZIP_CODE_TENDIGITS_REGEX = /^\d{5}-\d{1,5}$/;
export const POSTAL_CODE_REGEX =
  /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/;
export const CANADIAN_PHONE_NUMBER_REGEX = /^(\d{7}|\d{3}-\d{4})?$/;
export const INTERNATIONAL_PHONE_NUMBER_REGEX = /^\d{7,12}?$/;
export const PHONE_NUMBER_EXTENSION_REGEX = /^\d{1,10}?$/;
export const CANADIAN_AREA_CODE_REGEX = /^[1-9]\d{2}?$/;
export const INTERNATIONAL_AREA_CODE_REGEX = /^[1-9]\d{0,3}?$/;
export const ADDRESS_PHONE_VALIDATION_REGEX = /^[\d ()+-]{7,20}?$/;
export const NUMBER_WITH_NO_LEADING_ZEROS = /^(?![0.]+$)(?:[1-9]\d*|0)(?:\.\d{0,2})?$/;

export function requiredValidator(customErrorCode?: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (value !== undefined && value !== null && (value as any).length !== 0) {
      return {};
    } else {

      const customError: IErrorCode = {
        code: null,
        message: customErrorCode,
      };

      return customErrorCode ? customError : this.errorCode;
    }
  };
}

export function alphanumericValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      ALPHANUMERIC_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function numericValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      NUMERIC_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function faxValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      FAX_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function moneyPositiveValidator(allowZero: boolean = true): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      (allowZero && value >= 0) ||
      (!allowZero && value > 0)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function paymentAmountShouldNotHaveLeadingZeros(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (!!value && !NUMBER_WITH_NO_LEADING_ZEROS.test(value)
    ) {
      return this.errorCode;
    } else {
      return {};
    }
  };
}


export function paymentMinAmountValidator(minAmount: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (!!value && (value <= 0)
    ) {
      control['min'] = minAmount;
      return this.errorCode;
    } else {
      return {};
    }
  };
}

export function paymentMaxAmountValidator(maxAmount: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      !!value && value > maxAmount
    ) {
      control['max'] = maxAmount;
      return this.errorCode;
    } else {
      return {};
    }
  };
}

export function thirteenDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      THIRTEENDIGITS_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function percentageValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      PERCENTAGE_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      EMAIL_VALIDATOR.test(value as string)
    ) {
      return null;
    } else {
      return this.errorCode;
    }
  };
}

/**
 * B9 validator
 * @returns B9validator
 */
export function B9Validator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      B9_VALIDATION_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * B3 validator
 * @returns B3validator
 */
export function B3Validator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      B3_VALIDATION_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * BN15 validator
 * @returns BN15validator
 */
export function BN15Validator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      BN15_VALIDATION_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * Business Program validator
 * @returns B9validator
 */
export function ProgramIdValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      BUSINESS_PROGRAM_NO_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * Business Program validator - Number part only
 * @returns B4validator
 */
export function ProgramIdNumberOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      BUSINESS_PROGRAM_NO_ONLY_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * Equals to validator
 * @param comparand
 * @returns to validator
 */
export function equalToValidator(target: AbstractControl): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const toMatch = control;
    if (target.touched && toMatch.touched) {
      const isMatch = target.value === toMatch.value;
      // set equal value error on dirty controls
      if (!isMatch && target.valid && toMatch.valid) {
        toMatch.setErrors(this.errorCode);
        return this.errorCode;
      }

      if (isMatch && toMatch.getError('code') === this.errorCode.code) {
        toMatch.setErrors(null);
      }
    }
    return null;
  };
}

export function differentToValidator(
  comparand: string,
  customErrorCode?: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const group = control.parent;
    const target = group.controls[comparand];
    const toMatch = control;
    const isMatch =
      target.value === toMatch.value ||
      target.value?.replace(/\s+/g, ' ').trim()?.toLowerCase() ===
        toMatch.value?.replace(/\s+/g, ' ').trim()?.toLowerCase();

    const customError: IErrorCode = {
      code: null,
      message: customErrorCode,
    };

    if (isMatch) {
      toMatch.setErrors(this.errorCode);
      return customErrorCode ? customError : this.errorCode;
    }

    if (!isMatch && toMatch.getError('code') === this.errorCode.code) {
      toMatch.setErrors(null);
    }
    return null;
  };
}

/**
 * Minimums validator
 * @param min
 * @returns validator
 */
export function minimumValidator(min: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      value.length === 0 || // acceptable, otherwise field is required
      (value !== null && value.length >= min)
    ) {
      return {};
    } else {
      control['min'] = min;
      return this.errorCode;
    }
  };
}

/**
 * Carms maximum validator
 * @param max
 * @returns maximum validator
 */
export function maximumValidator(max: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value?.toString() : undefined;
    if (
      value === null ||
      value === undefined ||
      value.length === 0 || // acceptable, otherwise field is required
      (value !== null && value.length <= max)
    ) {
      return {};
    } else {
      control['max'] = max;
      return this.errorCode;
    }
  };
}

/**
 * Carms strict Maximum validator | Normalize 'EOL' characters to match BE
 * @param max
 * @returns strict maximum validator
 */
export function strictMaxLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value?.toString() : undefined;
    if (
      value === null || value === undefined ||
      value.length === 0 // acceptable, otherwise field is required
    ) {
      return {};
    } else {
      // Normalize returns to \r\n to accurately match backend length
      const normalizedLength = value.replace(/\r?\n/g, '\r\n').length;
      return normalizedLength > max ? this.errorCode : {};
    }
  };
}

/**
 * Carms lengthRange validator
 * @param min
 * @param max
 * @returns range validator
 */
export function rangeLengthValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      value.length === 0 || // acceptable, otherwise field is required
      (value !== null && value.length >= min && value.length <= max)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function checkOptionValidator(options: SelectOption[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (value !== null && options.map(o => o.key).includes(value)) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function emailEqualToValidator(
  comparand: string,
  acceptsEmpty: boolean = true,
): ValidatorFn {
  return (control: UntypedFormGroup): { [key: string]: any } => {
    const group = control.parent;
    const target = group.controls[comparand];
    const toMatch = control;
    const bothEmpty = target?.value?.trim()?.toLowerCase() === '' && toMatch?.value?.trim()?.toLowerCase() === '';

    if (bothEmpty && acceptsEmpty) {
      return null;
    }
    const isMatch = target?.value?.trim()?.toLowerCase() === toMatch?.value?.trim()?.toLowerCase();

    if (isMatch) {
      return null;
    } else {
      return this.errorCode;
    }
  };
}

export function minValueValidator(
  minValue: number,
  customErrorCode?: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (!value) {
      return {}; // if empty, required validator should flag.
    }

    const customError: IErrorCode = {
      code: 'MIN_VALUE',
      message: customErrorCode,
    };

    if (!isNaN(Number(value))) {
      const numberValue = Number(value);

      if (value && numberValue >= minValue) {
        return {};
      } else {
        return customErrorCode ? customError : this.errorCode;
      }
    } else {
      return customErrorCode ? customError : this.errorCode;
    }
  };
}

export function maxValueValidator(
  maxValue: number,
  customErrorCode?: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (!value) {
      return {}; // if empty, required validator should flag.
    }

    const customError: IErrorCode = {
      code: null,
      message: customErrorCode,
    };

    if (!isNaN(Number(value))) {
      const numberValue = Number(value);

      if (value && numberValue <= maxValue) {
        return {};
      } else {
        return customErrorCode ? customError : this.errorCode;
      }
    } else {
      return customErrorCode ? customError : this.errorCode;
    }
  };
}

export function registrationCarrierProgramIdValidator() {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      REGISTRATION_CARRIER_PROGRAMID_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function registrationWarehouseProgramIdValidator() {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      REGISTRATION_WAREHOUSE_PROGRAMID_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function registrationDutyFreeShopProgramIdValidator() {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      REGISTRATION_DUTYFREESHOP_PROGRAMID_REGEXP.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function allProgramAccessTypeRequiredValidator(
  comparand: string,
  equalTo: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    if (!control.parent) {
      return null;
    }
    const group = control.parent;
    const target = group.controls[comparand];
    const toMatch = control;
    // if (target.touched && toMatch.touched) {
    const isMatch = target.value === equalTo;
    const toMatchValue = toMatch ? toMatch.value : undefined;
    // set error if target control value equal to "equalTo" parameter value
    if (
      isMatch &&
      target.valid &&
      (toMatchValue === undefined ||
        toMatchValue === null ||
        (toMatchValue as any).length === 0)
    ) {
      toMatch.setErrors(this.errorCode);
      return this.errorCode;
    }

    if (!isMatch && toMatch.getError('code') === this.errorCode.code) {
      toMatch.setErrors(null);
    }
  };
}

export function specificProgramAccessTypeRequiredValidator(
  groupTypeSeclectorControl: string,
  programAccessGroup: string,
  resetValidationContol: string, // this is the control name which should reset in case this validation pass
  equalTo: string
): ValidatorFn {
  return (group: UntypedFormGroup): { [key: string]: any } => {
    const groupTypeSelectControl = group.get(groupTypeSeclectorControl);
    const programAccessFormGroup: UntypedFormGroup = group.get(
      programAccessGroup
    ) as UntypedFormGroup;

    if (groupTypeSelectControl && groupTypeSelectControl.value === equalTo) {
      for (const [, control] of Object.entries(
        programAccessFormGroup.controls
      )) {
        if (control.value !== '') {
          group.setErrors(null);
          if (group.get(resetValidationContol)) {
            group.get(resetValidationContol).setErrors(null);
          }
          return null;
        }
      }
      group.setErrors(this.errorCode);
      return this.errorCode;
    }
    group.setErrors(null);
    return null;
  };
}

/**
 * Date validator
 * @returns validator
 */
export function dateValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    const isValidDate = dayjs(value).isValid();
    if (!value || isValidDate) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * Minimums validator
 * @returns validator
 */
export function futureDateValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    const now = Date.now();
    if (value && value > now) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function todayOrLaterDateValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (value && value > setDateWithOffset(0, 0, -1)) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function dateLessThanOrEqualFunction(
  otherDate: Date,
  customErrorCode?: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    const customError: IErrorCode = {
      code: null,
      message: customErrorCode,
    };

    if (value && value <= otherDate) {
      return {};
    } else {
      return customErrorCode ? customError : this.errorCode;
    }
  };
}

export function dateGreaterThanOrEqualFunction(
  otherDate: Date,
  otherDateFunc?: () => Date,
  customErrorCode?: string
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    const otherDateValue =
      typeof otherDateFunc === 'function' ? otherDateFunc() : otherDate;

    const customError: IErrorCode = {
      code: null,
      message: customErrorCode,
    };

    if (value && value >= otherDateValue) {
      return {};
    } else {
      return customErrorCode ? customError : this.errorCode;
    }
  };
}

/**
 * Al least one value is true for all child controls
 * @returns validator
 */
export function atLeastOneValidatorFunction(): ValidatorFn {
  return (group: UntypedFormGroup): { [key: string]: any } => {
    let isValid = false;

    Object.keys(group.controls).forEach(key => {
      const controlValue = group.get(key).value;
      if (controlValue) {
        isValid = true;
      }
    });

    if (isValid) {
      group.setErrors(null);
    } else {
      group.setErrors(this.errorCode);
      return this.errorCode;
    }
  };
}

export function classificationNumberFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      CLASSIFICATION_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function acceptFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (value) {
      return {};
    }
    return this.errorCode;
  };
}

export function mustMatchWordFunction(matchWord: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if (value && matchWord.toUpperCase() === value.toUpperCase()) {
      return {};
    }
    return this.errorCode;
  };
}

export function hasAtLeast1AlphaCharacterFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      HAS_AT_LEAST_1_ALPHA_CHARACTER_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function hasAtLeast1AlphaNumericCharacterFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      HAS_AT_LEAST_1_ALPHANUM_CHARACTER_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function usZipCodeValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    if(value !== null && value.length === 5 && ZIP_CODE_REGEX.test(value as string)){
      if(control.errors){
        control.setErrors(null);
      }
      return {};
    } else if (value !== null  && value.length === 10 &&  ZIP_CODE_TENDIGITS_REGEX.test(value as string)) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function postalCodeValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      POSTAL_CODE_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function canadianPhoneNumberValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      CANADIAN_PHONE_NUMBER_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function internationalPhoneNumberValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      INTERNATIONAL_PHONE_NUMBER_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function phoneNumberExtensionValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      PHONE_NUMBER_EXTENSION_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function canadianAreaCodeValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      CANADIAN_AREA_CODE_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function internationalAreaCodeValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      INTERNATIONAL_AREA_CODE_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

/**
 * Minimums validator
 * @returns validator
 */
export function advancedFilterDateValidatorFunction(
  criteriaControl: AbstractControl
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const criteriaValue = criteriaControl ? criteriaControl.value : undefined;
    if (
      criteriaValue &&
      (criteriaValue.dataType === 'Date' ||
        criteriaValue.dataType === 'DateTimeOffset')
    ) {
      const value: string = control ? control.value : undefined;

      if (!value) {
        return {};
      }

      const parsedDate = new Date(value);
      const convertedString =
        value.substr(0, 4) +
        '-' +
        value.substr(4, 2) +
        '-' +
        value.substr(6, 2);
      if (
        !value ||
        value === '' ||
        (DATE_REGEX.test(value as string) &&
          parsedDate.toString() !== '') ||
        (DATE_REGEX_DIGITS.test(value as string) &&
          new Date(convertedString).toString() !== '')
      ) {
        return {};
      } else {
        return this.errorCode;
      }
    } else {
      return {};
    }
  };
}

/**
 * All values should be true for all child controls
 * @returns validator
 */
export function mustAcceptAllFunction(): ValidatorFn {
  return (group: UntypedFormGroup): { [key: string]: any } => {
    const isValid = Object.keys(group.controls).every(key => {
      const controlValue = group.get(key).value;
      return controlValue === true;
    });

    if (isValid) {
      group.setErrors(null);
    } else {
      group.setErrors(this.errorCode);
      return this.errorCode;
    }
  };
}

export function cadInvoiceDetailFormValidatorFunction(): ValidatorFn {
  return (group: UntypedFormGroup): { [key: string]: any } => {
    const oneOrOtherRequired: IErrorCode = {
      code: null,
      message: 'invoicePoNumber',
    };

    if (
      !group.controls['invoiceNumber'].value &&
      !group.controls['purchaseOrderNumber'].value
    ) {
      group.setErrors(oneOrOtherRequired);
      return oneOrOtherRequired;
    }

    group.setErrors(null);
  };
}


export function enrolmentPhoneNumberValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;
    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      ADDRESS_PHONE_VALIDATION_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function tempImportExpiryDateValidatorFunction(): ValidatorFn {
  return (group: UntypedFormGroup): { [key: string]: any } => {


    const arrivalDateControl = group.controls['arrivalDate'];
    const expiryDateControl = group.controls['expiryDate'];

    if (arrivalDateControl.value && expiryDateControl.value) {

      const arrivalDate = dayjs(arrivalDateControl.value, dateFormat);
      const expiryDate = dayjs(expiryDateControl.value, dateFormat);

      if(expiryDate <= arrivalDate) // arrival date must be greater than arrival date
      {
        const invalidExpiryDateMin: IErrorCode = {
          code: null,
          message: 'invalidTempImportExpiryDateMin',
        };

        expiryDateControl.setErrors(invalidExpiryDateMin);
        return invalidExpiryDateMin;
      }

      if(expiryDate > arrivalDate.add(18, 'months')) // expiry date cannot exceed 18 months past the arrival date
      {
        const invalidExpiryDateMax: IErrorCode = {
          code: null,
          message: 'invalidTempImportExpiryDateMax',
        };

        expiryDateControl.setErrors(invalidExpiryDateMax);
        return invalidExpiryDateMax;
      }
    }

    //expiryDateControl.setErrors(null);
  };
}


export function complianceCaseNoValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    const COMPLIANCE_CASE_NO_VALIDATION_REGEX = /^(RA|C)-\d{4}-\d{6}?$/; //HY-9157
    const COMPLIANCE_CASE_NO_VALIDATION_REGEX_RM = /^(C)-\d{4}-\d{6}?-\d{9}RM\d{4}?$/;

    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      COMPLIANCE_CASE_NO_VALIDATION_REGEX.test(value as string) ||
      COMPLIANCE_CASE_NO_VALIDATION_REGEX_RM.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function cadMaxQuantityValidatorFunction(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control ? control.value : undefined;

    const MAX_QUANTITY_REGEX = /^\d{1,10}(?:\.\d{1,3})?$/;

    if (
      value === null ||
      value === undefined ||
      (value as string).length === 0 ||
      MAX_QUANTITY_REGEX.test(value as string)
    ) {
      return {};
    } else {
      return this.errorCode;
    }
  };
}

export function phoneValidatorFunction(originalForm, includeCityCode = false): ValidatorFn {
  const phoneUtil: lpn.PhoneNumberUtil = lpn.PhoneNumberUtil.getInstance();
  return (control: AbstractControl): { [key: string]: any } => {
    // Validate phone number
    const phoneNumber: string = control?.value;
    const fGroup = control?.parent;
    const selectedCountryISO: string = fGroup ? fGroup?.get("countryDialCode")?.value?.iso2 : originalForm?.get("countryDialCode")?.value?.iso2;
    if (phoneNumber && selectedCountryISO) {
      // Preventing unnecessary calls to phoneUtil
      if (phoneNumber.length < 4) {
        return this.errorCode;
      }
      const cityCode = fGroup ? fGroup?.get("cityCode")?.value : originalForm?.get("cityCode");
      let isValidNumber = false;
      let isoPhoneNumber = includeCityCode ?
        phoneUtil.parseAndKeepRawInput(`${cityCode}${phoneNumber}`, selectedCountryISO) :
        phoneUtil.parseAndKeepRawInput(phoneNumber, selectedCountryISO);
      isValidNumber = phoneUtil.isValidNumber(isoPhoneNumber);
      // Validation rules
      if (isValidNumber) {
        return {};
      } else {
        return this.errorCode;
      }
    } else if (phoneNumber && !selectedCountryISO ) {
      return this.errorCode;
    } else {
      return {};
    }
  };
}